import React, { useState } from 'react';
import {
  Box,
  Typography,
  Toolbar,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Paper,
  CircularProgress
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PsychologyIcon from '@mui/icons-material/Psychology';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { TimerDisplay } from '../../common/TimerDisplay';
import { useLanguage } from '../../../i18n/LanguageContext';
import cloudIcon from '../../../assets/cloud_7747515.png';

interface WorkoutActionSectionProps {
  selectedDate: Date | null;
  bodyWeight: string;
  previousBodyWeight?: number | null;
  isAnalyzing: boolean;
  onOpenAnalysisDialog: () => void;
  suggestedCloneDate: Date | null;
  hasWorkoutsToday: boolean;
  isCloningWorkouts: boolean;
  onOpenCloneDialog: () => void;
  onOpenAddEntryDialog: () => void;
  onOpenBodyWeightDialog: () => void;
  onOpenImportRoutineDialog: () => void;
  onOpenRecommendationDialog: () => void;
  getRelativeDayName: (date: Date) => string;
}

export const WorkoutActionSection: React.FC<WorkoutActionSectionProps> = ({
  selectedDate,
  bodyWeight,
  previousBodyWeight,
  isAnalyzing,
  onOpenAnalysisDialog,
  suggestedCloneDate,
  hasWorkoutsToday,
  isCloningWorkouts,
  onOpenCloneDialog,
  onOpenAddEntryDialog,
  onOpenBodyWeightDialog,
  onOpenImportRoutineDialog,
  onOpenRecommendationDialog,
  getRelativeDayName,
}) => {
  const { t } = useLanguage();

  const [addWorkoutMenuAnchor, setAddWorkoutMenuAnchor] = useState<null | HTMLElement>(null);
  const addWorkoutMenuOpen = Boolean(addWorkoutMenuAnchor);

  const handleAddWorkoutMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAddWorkoutMenuAnchor(event.currentTarget);
  };

  const handleAddWorkoutMenuClose = () => {
    setAddWorkoutMenuAnchor(null);
  };

  const handleMenuItemClick = (callback: () => void) => {
    callback();
    setAddWorkoutMenuAnchor(null);
  };

  return (
    <>
      {/* Timer Display */}
      <TimerDisplay />

      {/* AI Analysis Card - Prominent action button */}
      <Paper
        elevation={3}
        sx={{
          mt: 2,
          mb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 2,
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          cursor: isAnalyzing ? 'default' : 'pointer',
          '&:hover': isAnalyzing ? {} : {
            transform: 'translateY(-4px)',
            boxShadow: 6,
          },
        }}
        onClick={isAnalyzing ? undefined : onOpenAnalysisDialog}
      >
        <Box
          sx={{
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isAnalyzing ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                <PsychologyIcon sx={{ color: 'white', fontSize: 28 }} />
              )}
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  mb: 0.5,
                }}
              >
                {t('workout.analyze')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.875rem',
                }}
              >
                {isAnalyzing ? t('workout.analyzing') || 'Analyzing...' : t('workout.analyzeDescription') || 'Get AI-powered insights about your workouts'}
              </Typography>
            </Box>
          </Box>
          {!isAnalyzing && (
            <TrendingUpIcon
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: 32,
              }}
            />
          )}
        </Box>
      </Paper>

      {/* Smart Actions Section - Conditional */}
      {suggestedCloneDate && !hasWorkoutsToday && (
        <Alert
          severity="info"
          icon={<AutoAwesomeIcon />}
          sx={{
            mt: 2,
            mb: 2,
            mx: 'auto',
            maxWidth: '600px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {t('calendar.smartActions')}:
            </Typography>
            <Button
              variant="contained"
              color="info"
              size="small"
              onClick={onOpenCloneDialog}
              disabled={isCloningWorkouts}
              startIcon={<img src={cloudIcon} alt="cloud" style={{ width: 16, height: 16 }} />}
            >
              {t('cloneWorkouts.smartButton', { day: getRelativeDayName(suggestedCloneDate) })}
            </Button>
          </Box>
        </Alert>
      )}

      {/* Bodyweight Display */}
      {selectedDate && (
        <Box sx={{ textAlign: 'center', mt: 2, mb: 1 }}>
          {bodyWeight ? (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="subtitle1" color="text.secondary">
                {`${t('calendar.bodyWeight')}: ${bodyWeight} kg`}
              </Typography>
              {previousBodyWeight !== null && previousBodyWeight !== undefined && (() => {
                const diff = parseFloat(bodyWeight) - previousBodyWeight;
                if (diff === 0) return null;
                const isUp = diff > 0;
                return (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', color: isUp ? 'error.main' : 'success.main' }}>
                    {isUp
                      ? <ArrowUpwardIcon fontSize="small" />
                      : <ArrowDownwardIcon fontSize="small" />
                    }
                    <Typography component="span" variant="subtitle1">
                      {Math.abs(diff).toFixed(1)} kg
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
          ) : (
            <Typography variant="subtitle1" color="text.secondary">
              {t('calendar.noBodyWeight')}
            </Typography>
          )}
        </Box>
      )}

      {/* Main Actions Toolbar */}
      <Toolbar sx={{ justifyContent: 'center', gap: 2, mt: 2, mb: 1, p: '0 !important', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          onClick={handleAddWorkoutMenuClick}
          disabled={!selectedDate}
          endIcon={<ArrowDropDownIcon />}
          sx={{ minWidth: '200px' }}
        >
          {t('calendar.addWorkout')}
        </Button>

        {selectedDate && (
          <Button
            variant="contained"
            onClick={onOpenBodyWeightDialog}
            sx={{ minWidth: '200px' }}
          >
            {bodyWeight ? t('bodyweight.updateButton') : t('bodyweight.logButton')}
          </Button>
        )}
      </Toolbar>

      {/* Add Workout Dropdown Menu */}
      <Menu
        anchorEl={addWorkoutMenuAnchor}
        open={addWorkoutMenuOpen}
        onClose={handleAddWorkoutMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuItem onClick={() => handleMenuItemClick(onOpenAddEntryDialog)}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('calendar.addWorkoutEntry')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleMenuItemClick(onOpenRecommendationDialog)} disabled={!selectedDate}>
          <ListItemIcon>
            <img src={cloudIcon} alt="AI" style={{ width: 20, height: 20 }} />
          </ListItemIcon>
          <ListItemText>{t('recommendation.button')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleMenuItemClick(onOpenImportRoutineDialog)} disabled={!selectedDate || hasWorkoutsToday}>
          <ListItemIcon>
            <FolderIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('importRoutine.button')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => handleMenuItemClick(onOpenCloneDialog)} disabled={!selectedDate || hasWorkoutsToday}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('cloneWorkouts.button')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
